import { ErrorCode, StructuredError } from './errors';
import { toast } from 'sonner';
import { getUserFriendlyMessage, ERROR_MESSAGES } from './errorMessages';
import { ZodError } from 'zod';

export class ErrorHandler {
  /**
   * Parse edge function errors from Supabase responses
   */
  static parseEdgeError(error: any): StructuredError {
    // Check if error.context.body contains our structured error
    if (error?.context?.body?.error && typeof error.context.body.error === 'object') {
      const e = error.context.body.error;
      return {
        code: e.code || ErrorCode.UNKNOWN_ERROR,
        message: e.message || getUserFriendlyMessage(error),
        details: e.details,
        field: e.field,
        retryable: e.retryable ?? true,
        action: e.action
      };
    }
    
    // Check for error message in context body as string
    if (error?.context?.body?.error && typeof error.context.body.error === 'string') {
      return {
        code: ErrorCode.UNKNOWN_ERROR,
        message: getUserFriendlyMessage({ message: error.context.body.error }),
        details: error.context.body.error,
        retryable: true
      };
    }
    
    // Check top-level error object
    if (error?.error && typeof error.error === 'object' && error.error.code) {
      return {
        code: error.error.code,
        message: error.error.message || getUserFriendlyMessage(error.error),
        details: error.error.details,
        field: error.error.field,
        retryable: error.error.retryable ?? true,
        action: error.error.action
      };
    }
    
    // Fallback to message parsing
    return {
      code: ErrorCode.UNKNOWN_ERROR,
      message: getUserFriendlyMessage(error),
      details: JSON.stringify(error),
      retryable: true
    };
  }

  /**
   * Parse database errors
   */
  static parseDatabaseError(error: any): StructuredError {
    const code = error?.code;
    const message = error?.message || '';
    
    if (code === '23505' || message.includes('duplicate')) {
      return {
        code: ErrorCode.DB_CONSTRAINT_VIOLATION,
        message: ERROR_MESSAGES.database.duplicateRecord,
        details: message,
        retryable: false,
        action: 'Use a different name or identifier'
      };
    }
    
    if (code === '42501' || message.includes('permission') || message.includes('policy')) {
      return {
        code: ErrorCode.DB_RLS_VIOLATION,
        message: ERROR_MESSAGES.database.permissionDenied,
        details: message,
        retryable: false,
        action: 'Sign in again or contact support'
      };
    }
    
    if (message.includes('connection') || message.includes('timeout')) {
      return {
        code: ErrorCode.DB_CONNECTION_FAILED,
        message: ERROR_MESSAGES.database.connectionFailed,
        details: message,
        retryable: true,
        action: 'Check your internet connection and retry'
      };
    }

    if (message.includes('not found') || code === 'PGRST116') {
      return {
        code: ErrorCode.DB_NOT_FOUND,
        message: ERROR_MESSAGES.database.notFound,
        details: message,
        retryable: false
      };
    }
    
    return {
      code: ErrorCode.DB_QUERY_FAILED,
      message: ERROR_MESSAGES.database.queryFailed,
      details: message,
      retryable: true
    };
  }

  /**
   * Parse Zod validation errors
   */
  static parseValidationError(error: ZodError): StructuredError {
    const firstIssue = error.issues[0];
    const fieldPath = firstIssue.path.join('.');
    const fieldName = fieldPath.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    let message: string;
    
    switch (firstIssue.code) {
      case 'invalid_type':
        if (firstIssue.received === 'undefined' || firstIssue.received === 'null') {
          message = ERROR_MESSAGES.form.required(fieldName);
        } else {
          message = `${fieldName} has an invalid format.`;
        }
        break;
      case 'too_small':
        message = ERROR_MESSAGES.form.minLength(fieldName, firstIssue.minimum as number);
        break;
      case 'too_big':
        message = ERROR_MESSAGES.form.maxLength(fieldName, firstIssue.maximum as number);
        break;
      case 'invalid_string':
        if (firstIssue.validation === 'email') {
          message = ERROR_MESSAGES.form.invalidEmail;
        } else {
          message = `${fieldName} format is invalid.`;
        }
        break;
      default:
        message = firstIssue.message || `${fieldName} is invalid.`;
    }

    return {
      code: ErrorCode.VALIDATION_FAILED,
      message,
      field: fieldPath,
      retryable: true,
      action: 'Please correct the highlighted field'
    };
  }

  /**
   * Parse file upload errors
   */
  static parseUploadError(error: any): StructuredError {
    const message = error?.message?.toLowerCase() || '';
    
    if (message.includes('size') || message.includes('large')) {
      return {
        code: ErrorCode.FILE_TOO_LARGE,
        message: ERROR_MESSAGES.upload.fileTooLarge('2MB'),
        details: error.message,
        retryable: true,
        action: 'Choose a smaller file'
      };
    }
    
    if (message.includes('type') || message.includes('format')) {
      return {
        code: ErrorCode.FILE_INVALID_TYPE,
        message: ERROR_MESSAGES.upload.invalidFileType('Excel (.xlsx) or CSV'),
        details: error.message,
        retryable: true,
        action: 'Select a valid file format'
      };
    }
    
    if (message.includes('parse') || message.includes('read')) {
      return {
        code: ErrorCode.FILE_PARSE_ERROR,
        message: ERROR_MESSAGES.upload.parseError,
        details: error.message,
        retryable: true,
        action: 'Check that the file is not corrupted'
      };
    }
    
    if (message.includes('empty')) {
      return {
        code: ErrorCode.FILE_EMPTY,
        message: ERROR_MESSAGES.upload.emptyFile,
        details: error.message,
        retryable: true,
        action: 'Select a file with data'
      };
    }
    
    return {
      code: ErrorCode.FILE_UPLOAD_FAILED,
      message: ERROR_MESSAGES.upload.uploadFailed,
      details: error.message,
      retryable: true
    };
  }

  /**
   * Parse network/fetch errors
   */
  static parseNetworkError(error: any): StructuredError {
    const message = error?.message?.toLowerCase() || '';
    
    if (message.includes('offline') || message.includes('network')) {
      return {
        code: ErrorCode.NETWORK_OFFLINE,
        message: ERROR_MESSAGES.network.offline,
        details: error.message,
        retryable: true,
        action: 'Check your internet connection'
      };
    }
    
    if (message.includes('timeout') || message.includes('aborted')) {
      return {
        code: ErrorCode.NETWORK_TIMEOUT,
        message: ERROR_MESSAGES.network.timeout,
        details: error.message,
        retryable: true,
        action: 'Try again'
      };
    }
    
    if (message.includes('500') || message.includes('server')) {
      return {
        code: ErrorCode.SERVER_ERROR,
        message: ERROR_MESSAGES.network.serverError,
        details: error.message,
        retryable: true,
        action: 'Try again later'
      };
    }
    
    return {
      code: ErrorCode.UNKNOWN_ERROR,
      message: getUserFriendlyMessage(error),
      details: error.message,
      retryable: true
    };
  }

  /**
   * Show error toast with proper formatting
   */
  static showError(error: StructuredError, options?: {
    duration?: number;
    onRetry?: () => void;
  }) {
    const description = error.action 
      ? `${error.message}\n\n${error.action}`
      : error.message;

    toast.error('Error', {
      description,
      duration: options?.duration || (error.retryable ? 5000 : 7000),
      action: error.retryable && options?.onRetry ? {
        label: 'Retry',
        onClick: options.onRetry
      } : undefined
    });

    // Log technical details (without exposing to user)
    if (error.details) {
      console.error(`[Error ${ErrorCode[error.code]}]:`, error.details);
    }
  }

  /**
   * Handle analytics-specific errors
   */
  static handleAnalyticsError(error: any, context: string): StructuredError {
    if (error?.message?.includes('not found') || error?.code === 'PGRST116') {
      return {
        code: ErrorCode.DB_QUERY_FAILED,
        message: 'Some analytics data could not be loaded. Please refresh the page.',
        details: error.message,
        retryable: true,
        action: 'Try refreshing the page or adjusting filters'
      };
    }
    return this.handle(error, context);
  }

  /**
   * Handle errors from any source - main entry point
   */
  static handle(error: any, context: string, onRetry?: () => void): StructuredError {
    let structuredError: StructuredError;

    // Check if it's already a structured error
    if (error?.code && error?.message && typeof error.code === 'number' && error.retryable !== undefined) {
      structuredError = error;
    }
    // Handle Zod validation errors
    else if (error instanceof ZodError) {
      structuredError = this.parseValidationError(error);
    }
    // Parse edge function errors
    else if (error?.context || (error?.error && typeof error.error === 'object')) {
      structuredError = this.parseEdgeError(error);
    }
    // Parse database errors (PostgreSQL error codes)
    else if (error?.code && (typeof error.code === 'string' && error.code.length === 5) || error?.hint) {
      structuredError = this.parseDatabaseError(error);
    }
    // Parse network/fetch errors
    else if (error?.name === 'TypeError' && error?.message?.includes('fetch')) {
      structuredError = this.parseNetworkError(error);
    }
    // Generic error
    else {
      structuredError = {
        code: ErrorCode.UNKNOWN_ERROR,
        message: getUserFriendlyMessage(error),
        details: error?.message || JSON.stringify(error),
        retryable: true
      };
    }

    // Add context to logging
    console.error(`[${context}]`, structuredError);

    this.showError(structuredError, { onRetry });
    
    return structuredError;
  }
}
